import { useState } from 'react';
import { Headphones, FileText, Loader2 } from 'lucide-react';
import { ApiError, NetworkError } from '@/lib/api-client';
import { retrofitAudio, retrofitPdf } from '@/features/story/api';
import { STRINGS } from '@/strings/strings';
import type { PdfStatus, StoryAudioStatus } from '@/features/story/types';
import { RetrofitGenderSheet, type NarratorGender } from './RetrofitGenderSheet';

type Variant = 'audio' | 'pdf';

interface Props {
  storyId: string;
  variant: Variant;
  /**
   * Current asset status from the parent's view of the story. Controls
   * the render mode: button vs. non-interactive pending hint vs. null.
   */
  currentStatus: StoryAudioStatus | PdfStatus;
  /**
   * Fired after the backend returns 202 for the retrofit POST. Parents
   * use this to optimistically flip their local status to 'pending' so
   * (a) the button re-renders as the pending hint, and (b) any parent
   * polling loop kicks in and eventually flips status to
   * completed/failed. Payload contains the new pending status so the
   * parent doesn't have to know which variant this button drives.
   */
  onEnqueued?: (nextStatus: 'pending') => void;
  /** Optional test id passthrough. */
  testId?: string;
}

const AUDIO_ELIGIBLE: readonly StoryAudioStatus[] = ['not_requested', 'failed'];
const AUDIO_PENDING: readonly StoryAudioStatus[] = ['pending', 'generating', 'retrying'];
const PDF_ELIGIBLE: readonly PdfStatus[] = ['not_requested', 'failed'];
const PDF_PENDING: readonly PdfStatus[] = ['pending', 'generating'];

/**
 * FIX A — Retrofit trigger for audio or PDF on an existing story.
 *
 * Render matrix (identical for both variants):
 *   - eligible status (`not_requested` | `failed`)
 *       → clickable button. Audio variant opens
 *         RetrofitGenderSheet on click, PDF variant fires the POST
 *         directly. Errors surface inline underneath the button
 *         (backend user_message on 409, fallback copy on network error).
 *   - pending status (`pending` | `generating` | `retrying` for audio;
 *     `pending` | `generating` for PDF)
 *       → non-interactive pulsing pending hint ("Audio wird erstellt …"
 *         / "PDF wird erstellt …"). Same visual language as the
 *         existing AudioStatusPlaceholder / PdfPlaceholderButton
 *         generating states so the transition from click → optimistic
 *         pending → server pending is seamless.
 *   - anything else (completed/ready)
 *       → null (asset already exists; the existing icons take over).
 *
 * Credit-safety: the POST hits the retrofit endpoint, which enqueues
 * directly onto story-audio / story-pdf and NEVER re-enters the
 * story-generation pipeline. Backend Block 1 tests lock: zero
 * credit_transactions rows created, storiesUsedThisMonth unchanged.
 *
 * IRON RULE observed: this component does not touch audioStore, does
 * not render inside MiniPlayer / NowPlaying, does not manage audio
 * playback. It only fires an API POST and displays state.
 */
export function RetrofitAssetButton({
  storyId,
  variant,
  currentStatus,
  onEnqueued,
  testId,
}: Props) {
  const S = STRINGS.retrofit;
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const isPending =
    variant === 'audio'
      ? AUDIO_PENDING.includes(currentStatus as StoryAudioStatus)
      : PDF_PENDING.includes(currentStatus as PdfStatus);
  const isEligible =
    variant === 'audio'
      ? AUDIO_ELIGIBLE.includes(currentStatus as StoryAudioStatus)
      : PDF_ELIGIBLE.includes(currentStatus as PdfStatus);

  if (isPending) {
    return (
      <div
        aria-live="polite"
        className="h-9 rounded-md bg-dream-gold/25 px-3 flex items-center justify-center text-xs font-medium text-dream-navy animate-pulse motion-reduce:animate-none"
        data-testid={testId ? `${testId}-pending` : undefined}
      >
        {variant === 'audio' ? S.audioPending : S.pdfPending}
      </div>
    );
  }

  if (!isEligible) return null;

  const resolveErrorMessage = (err: unknown): string => {
    if (err instanceof ApiError && err.envelope?.user_message) {
      return err.envelope.user_message;
    }
    if (err instanceof NetworkError) return S.errorFallback;
    return S.errorFallback;
  };

  const firePdf = async () => {
    setError(null);
    setInFlight(true);
    try {
      await retrofitPdf(storyId);
      onEnqueued?.('pending');
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setInFlight(false);
    }
  };

  const fireAudio = async (gender: NarratorGender) => {
    setError(null);
    setInFlight(true);
    try {
      await retrofitAudio(storyId, gender);
      onEnqueued?.('pending');
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setInFlight(false);
    }
  };

  const handleClick = () => {
    if (variant === 'audio') {
      setSheetOpen(true);
    } else {
      void firePdf();
    }
  };

  const label = variant === 'audio' ? S.audioButton : S.pdfButton;
  const Icon = variant === 'audio' ? Headphones : FileText;

  return (
    <>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={handleClick}
          disabled={inFlight}
          className="inline-flex h-9 items-center gap-1.5 self-start rounded-md border-[1.5px] border-dream-navy px-3 text-xs font-medium text-dream-navy transition hover:bg-dream-navy/5 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid={testId}
          aria-label={label}
        >
          {inFlight ? (
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          ) : (
            <Icon aria-hidden className="h-4 w-4" />
          )}
          {label}
        </button>
        {error && (
          <p
            role="alert"
            className="text-xs text-error"
            data-testid={testId ? `${testId}-error` : undefined}
          >
            {error}
          </p>
        )}
      </div>
      {variant === 'audio' && (
        <RetrofitGenderSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          onConfirm={(gender) => {
            void fireAudio(gender);
          }}
        />
      )}
    </>
  );
}
